import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, CircularProgress, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { MdiIcon, Table, useSnackbar } from '@components';
import { mdiFolderNetwork, mdiMagnify, mdiSync } from '@mdi/js';
import moment from 'moment';
import { useModal } from 'mui-modal-provider';
// CR-SLD20180014-0019: o servidor devolve estas datas em UTC («AAAA-MM-DDTHH:mm:ssZ»); o dia escolhido
// (hora local) passa ao intervalo UTC equivalente. Devolve undefined para os outros campos/operadores.
export const filtroDataInteracao = (r) => {
    if (!['dataUltimaInteracao', 'dataProximaInteracao'].includes(r.id))
        return undefined;
    const utc = (m) => m.utc().format('YYYY-MM-DDTHH:mm:ss[Z]');
    const inicio = utc(moment(r.value).startOf('day'));
    const fim = utc(moment(r.value).endOf('day'));
    switch (r.type) {
        case 'equals':
            return `$btn:${inicio};${fim}`;
        case 'notEquals':
            return `$nbtn:${inicio};${fim}`;
        case 'greaterThan':
            return `$gt:${fim}`;
        case 'greaterThanOrEqualTo':
            return `$gte:${inicio}`;
        case 'lessThan':
            return `$lt:${inicio}`;
        case 'lessThanOrEqualTo':
            return `$lte:${fim}`;
        default:
            return undefined;
    }
};
const PropostasList = ({ $state, CrmService, client, vendedor }) => {
    const [data, setData] = useState();
    const [loading, setLoading] = useState(true);
    const [refetching, setRefetching] = useState(false);
    const ultimoPedido = useRef(0); // só a resposta do pedido mais recente chega à tabela (o filtro pode mudar a meio)
    const [filter, setFilter] = useState([]); // filtros a aplicar na tabela
    const [sort, setSort] = useState([]); // ordenação da tabela
    //Constantes para sincronizacao de propostas
    const [syncing, setSyncing] = useState(false);
    const getProposalFolderPath = (refPropostaPrimavera, refPropostaData) => {
        if (!refPropostaPrimavera || !refPropostaData)
            return '';
        // Vai buscar o ano da data
        const year = new Date(refPropostaData).getFullYear();
        // Remove espaços e /
        const formattedRef = refPropostaPrimavera.replace(/\s+/g, '').replace(/\//g, '').replace(/\./g, '');
        return `\\\\servernas\\COMUM COM\\PROPOSTAS ${year}\\${formattedRef}`;
    };
    const copyProposalFolder = async (refPropostaPrimavera, refPropostaData) => {
        if (!refPropostaPrimavera || !refPropostaData) {
            showSnackbar('Sem informação a copiar');
            return;
        }
        const path = getProposalFolderPath(refPropostaPrimavera, refPropostaData);
        try {
            if (!navigator.clipboard) {
                // Fallback
                const tempInput = document.createElement('input');
                tempInput.type = 'text';
                tempInput.value = path;
                document.body.appendChild(tempInput);
                tempInput.select();
                tempInput.setSelectionRange(0, 99999);
                document.execCommand('copy');
                document.body.removeChild(tempInput);
                showSnackbar('Caminho copiado para a Área de Transferência');
            }
            else {
                await navigator.clipboard.writeText(path);
                showSnackbar('Caminho copiado para a Área de Transferência');
            }
        }
        catch (err) {
            console.error(err);
            showSnackbar('Erro na cópia para a Área de Transferência');
        }
    };
    const [options, setOptions] = useState({
        filter: [], // exemplo -> [{name: 'nome', type: 'TEXT'}, {name: 'ultimoContacto', type: 'DATE', options: {format: 'YYYY-MM-DD'}}, ...]
        sort: [] // exemplo -> ['id', 'nome', 'ultimoContacto', 'contactoTelefonico', 'nif', 'email', 'morada', 'localidade']
    });
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: 10
    });
    const { showSnackbar } = useSnackbar();
    const { showModal } = useModal();
    // Filtragem dentro de cada coluna
    // Modo de filtro (igual a, contém, começa com... )
    const getType = (type) => {
        switch (type) {
            case 'contains':
                return '$ilike';
            case 'startsWith':
                return '$sw';
            case 'notEquals':
                return '$not';
            case 'empty':
                return '$null';
            case 'notEmpty':
                return '$notNull';
            case 'greaterThan':
                return '$gt';
            case 'greaterThanOrEqualTo':
                return '$gte';
            case 'lessThan':
                return '$lt';
            case 'lessThanOrEqualTo':
                return '$lte';
            default:
                return '$eq';
        }
    };
    const columns = useMemo(() => [
        {
            accessorKey: 'refPropostaPrimavera',
            header: 'Número Documento'
        },
        {
            accessorKey: 'data',
            header: 'Data',
            Cell: ({ row }) => (moment(row.original.data).isValid() ? moment(row.original.data).format('DD/MM/YYYY') : 'N/D')
        },
        {
            accessorKey: 'dataUltimaInteracao',
            header: 'Última Interação',
            sortDescFirst: false, // a 1.ª linha pode vir vazia e o MRT deduziria a ordem a partir dela
            Cell: ({ renderedCellValue }) => (renderedCellValue ? moment(renderedCellValue).format('DD/MM/YYYY HH:mm') : '-')
        },
        {
            accessorKey: 'dataProximaInteracao',
            header: 'Próxima Interação',
            sortDescFirst: false, // a 1.ª linha pode vir vazia e o MRT deduziria a ordem a partir dela
            Cell: ({ renderedCellValue }) => (renderedCellValue ? moment(renderedCellValue).format('DD/MM/YYYY HH:mm') : '-')
        },
        {
            accessorKey: 'nome',
            header: 'Designação'
        },
        {
            accessorKey: 'precoTotal',
            header: 'Preço'
        }
    ], []);
    const initialFetch = async () => {
        if (loading) {
            // opt -> vai buscar os campos filtraveis
            const opt = await CrmService.optionsPropostas();
            setOptions(opt);
            setLoading(false);
        }
    };
    const fetchData = async () => {
        const pedido = ++ultimoPedido.current;
        try {
            let filters = {};
            filter.forEach(r => {
                filters[r.id] = filtroDataInteracao(r) ?? `${getType(r.type)}:${r.value}`;
            });
            let sorter = [];
            sort.forEach(r => {
                sorter.push(`${r.id}:${r.desc ? 'DESC' : 'ASC'}`);
            });
            if (client !== undefined) {
                filters['clienteId'] = `$eq:${client}`;
            }
            if (vendedor !== undefined) {
                filters['funcionarioId'] = `$eq:${vendedor}`;
            }
            const aux = await CrmService.findAllPropostasWitNextInteraction({
                limit: pagination.pageSize,
                page: pagination.pageIndex + 1,
                filter: filters,
                sortBy: sorter
            });
            if (pedido !== ultimoPedido.current)
                return;
            console.log(aux);
            // Update URL
            $state.go($state.current.name || '', {
                limit: pagination.pageSize,
                page: pagination.pageIndex + 1,
                sortBy: sorter,
                filter: Object.keys(filter).length > 0 ? JSON.stringify(filters) : undefined
            }, {
                notify: false,
                reload: false,
                location: 'replace',
                inherit: true
            });
            setData(aux);
        }
        catch (e) { }
        if (pedido === ultimoPedido.current)
            setRefetching(false);
    };
    useEffect(() => {
        initialFetch();
        setRefetching(true);
        fetchData();
    }, [pagination, filter, sort]);
    return (<Box>
      <Stack direction='row' justifyContent='space-between' alignItems='center'>
        <Typography variant='h6' sx={{ color: 'black' }}>
          Propostas - Primavera
        </Typography>

        {client && (<Tooltip title={'Importar Propostas'} placement='top'>
            <IconButton color='primary' disabled={syncing} onClick={async () => {
                try {
                    setSyncing(true);
                    const res = await CrmService.allFromPrimaveraForClient(client, undefined, undefined);
                    const imported = res.filter((p) => p.imported === true);
                    showSnackbar(`Importação concluida com sucesso: ${imported.length > 0 ? ` Foram adicionadas ${imported.length}  propostas` : 'Não foram adicionadas novas propostas'}`);
                    fetchData();
                }
                catch (err) {
                    console.error(err);
                }
                finally {
                    setSyncing(false);
                }
            }}>
              {syncing ? <CircularProgress size={20}/> : <MdiIcon path={mdiSync} sx={{ color: '#1976D2' }}/>}
            </IconButton>
          </Tooltip>)}
      </Stack>

      <Box sx={{
            borderRadius: '15px',
            border: '1px solid #e0e0e0',
            overflow: 'hidden',
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)'
        }}>
        <Table storageKey={$state.current.name || ''} data={data} columns={columns} initial={{
            sort: sort,
            filter: filter
        }} isLoading={refetching} options={options} onPaginate={setPagination} onFilter={setFilter} onSort={setSort} rowActionSize={100} onDoubleClick={row => {
            window.open($state.href('app.crm.propostas.details', { id: row.id }, { absolute: true }));
        }} actionItems={({ row }) => [
            <Box key={`actions-${row.original.id}`} gap={1} sx={{
                    width: '100%', // Ocupa toda a largura da célula
                    height: '100%', // Ocupa toda a altura da célula
                    display: 'flex',
                    justifyContent: 'center', // Centra horizontalmente
                    alignItems: 'center' // Centra verticalmente
                }}>
              <Tooltip title={'Ver detalhes'} placement='top'>
                <IconButton color='secondary' onClick={() => {
                    window.open($state.href('app.crm.propostas.details', { id: row.id }, { absolute: true }));
                }}>
                  <MdiIcon path={mdiMagnify} sx={{ color: '#1976D2' }}/>
                </IconButton>
              </Tooltip>
              <Tooltip title={'Copiar caminho da pasta'} placement='top'>
                <IconButton color='primary' onClick={() => {
                    copyProposalFolder(row.original.refPropostaPrimavera, row.original.data);
                }}>
                  <MdiIcon path={mdiFolderNetwork} sx={{ color: '#1976D2' }}/>
                </IconButton>
              </Tooltip>
            </Box>
        ]}></Table>
      </Box>
    </Box>);
};
export default PropostasList;
